import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { API_BASE as API } from '../lib/apiBase';

const PlayerContext = createContext();

export function PlayerProvider({ children }) {
  const audioRef = useRef(new Audio());
  const [currentSong, setCurrentSong] = useState(null);
  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem('pulse_volume');
    return saved ? parseFloat(saved) : 0.8;
  });
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState('none');
  const [autoplay, setAutoplay] = useState(() => localStorage.getItem('pulse_autoplay') !== 'false');
  const [expanded, setExpanded] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const savedSong = localStorage.getItem('pulse_current_song');
    const savedQueue = localStorage.getItem('pulse_queue');
    if (savedSong) {
      try { setCurrentSong(JSON.parse(savedSong)); } catch (e) {}
    }
    if (savedQueue) {
      try { setQueue(JSON.parse(savedQueue)); } catch (e) {}
    }
  }, []);

  useEffect(() => {
    if (currentSong) localStorage.setItem('pulse_current_song', JSON.stringify(currentSong));
    localStorage.setItem('pulse_volume', volume.toString());
    localStorage.setItem('pulse_autoplay', autoplay ? 'true' : 'false');
  }, [autoplay, currentSong, volume]);

  useEffect(() => {
    if (queue.length > 0) localStorage.setItem('pulse_queue', JSON.stringify(queue));
  }, [queue]);

  const resolveAudioUrl = useCallback((song) => {
    const source = song?.audio_url;
    if (!source) return '';
    if (/^https?:\/\//i.test(source)) return source;
    if (source.startsWith('/')) return `${API}${source}`;
    return `${API}/${source.replace(/^\/+/, '')}`;
  }, []);

  const playSong = useCallback(async (song, newQueue = null, index = 0) => {
    const source = resolveAudioUrl(song);
    if (!song || !source) return;

    if (newQueue) {
      setQueue(newQueue);
      setQueueIndex(index);
    } else if (queue.length === 0) {
      setQueue([song]);
      setQueueIndex(index);
    }

    setCurrentSong(song);
    setCurrentTime(0);
    setDuration(song.duration || 0);

    const audio = audioRef.current;
    audio.pause();
    audio.removeAttribute('crossorigin');
    audio.preload = 'auto';
    audio.src = source;
    audio.currentTime = 0;
    audio.load();

    try {
      await audio.play();
      setIsPlaying(true);
    } catch (error) {
      console.error('[Player] Play blocked or failed:', error?.message || error);
      setIsPlaying(false);
    }

    setHistory((prev) => [song, ...prev.filter((item) => item?.id !== song.id).slice(0, 49)]);

    if (song.id) {
      fetch(`${API}/api/songs/${song.id}/play`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device: /Mobi/i.test(navigator.userAgent) ? 'mobile' : 'web' })
      }).catch(() => {});
    }
  }, [queue.length, resolveAudioUrl]);

  const fetchAutoplayMix = useCallback(async (seedSong) => {
    if (!seedSong?.id && !seedSong?.artist_id) return [];
    try {
      const token = localStorage.getItem('pulse_token');
      const params = new URLSearchParams({ song_id: seedSong?.id || '', artist_id: seedSong?.artist_id || '', limit: '12' });
      const response = await fetch(`${API}/api/discover/radio?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await response.json();
      return (data.songs || []).filter((item) => item?.id && item.id !== seedSong?.id);
    } catch {
      return [];
    }
  }, []);

  const handleNext = useCallback(async () => {
    if (!queue.length) {
      setIsPlaying(false);
      return;
    }

    if (repeat === 'one') {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => setIsPlaying(false));
      return;
    }

    let nextIdx = shuffle ? Math.floor(Math.random() * queue.length) : queueIndex + 1;
    if (nextIdx >= queue.length) {
      if (repeat === 'all') nextIdx = 0;
      else if (autoplay && currentSong) {
        const radioSongs = await fetchAutoplayMix(currentSong);
        if (radioSongs.length > 0) {
          const existingIds = new Set(queue.map(item => item?.id));
          const extension = radioSongs.filter(item => item?.id && !existingIds.has(item.id));
          if (extension.length > 0) {
            const mergedQueue = [...queue, ...extension];
            const targetIndex = queue.length;
            setQueue(mergedQueue);
            setQueueIndex(targetIndex);
            playSong(mergedQueue[targetIndex], mergedQueue, targetIndex);
            return;
          }
        }
        setIsPlaying(false);
        return;
      } else {
        setIsPlaying(false);
        return;
      }
    }

    if (queue[nextIdx]) {
      setQueueIndex(nextIdx);
      playSong(queue[nextIdx], null, nextIdx);
    }
  }, [autoplay, currentSong, fetchAutoplayMix, playSong, queue, queueIndex, repeat, shuffle]);

  const handlePrev = useCallback(() => {
    if (currentTime > 3) {
      audioRef.current.currentTime = 0;
      return;
    }
    const prevIdx = queueIndex - 1;
    if (prevIdx >= 0 && queue[prevIdx]) {
      setQueueIndex(prevIdx);
      playSong(queue[prevIdx], null, prevIdx);
    }
  }, [currentTime, playSong, queue, queueIndex]);

  useEffect(() => {
    const audio = audioRef.current;
    const onTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
    const onLoadedMetadata = () => setDuration(audio.duration || currentSong?.duration || 0);
    const onEnded = () => handleNext();
    const onPlaying = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onError = () => {
      console.error('[Player] Audio error:', audio.error?.message || 'unknown error');
      if (queue.length > 1) handleNext();
      else setIsPlaying(false);
    };

    audio.volume = volume;
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('error', onError);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('error', onError);
    };
  }, [currentSong?.duration, handleNext, queue.length, volume]);

  useEffect(() => {
    audioRef.current.volume = volume;
  }, [volume]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!currentSong) return;
    if (isPlaying) {
      audio.pause();
      return;
    }
    try {
      await audio.play();
      setIsPlaying(true);
    } catch (error) {
      console.error('[Player] Resume failed:', error?.message || error);
      setIsPlaying(false);
    }
  }, [currentSong, isPlaying]);

  const seek = useCallback((time) => {
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  }, []);

  const addToQueue = useCallback((song) => {
    setQueue((prev) => [...prev, song]);
  }, []);

  const removeFromQueue = useCallback((index) => {
    setQueue((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearPlayer = useCallback(() => {
    const audio = audioRef.current;
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
    setCurrentSong(null);
    setQueue([]);
    setQueueIndex(0);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setExpanded(false);
    localStorage.removeItem('pulse_current_song');
    localStorage.removeItem('pulse_queue');
  }, []);

  return (
    <PlayerContext.Provider value={{
      currentSong,
      queue,
      queueIndex,
      isPlaying,
      currentTime,
      duration,
      volume,
      shuffle,
      repeat,
      autoplay,
      expanded,
      history,
      playSong,
      togglePlay,
      handleNext,
      handlePrev,
      seek,
      setVolume,
      setShuffle,
      setRepeat: (value) => setRepeat(value),
      toggleRepeat: () => setRepeat((value) => value === 'none' ? 'all' : value === 'all' ? 'one' : 'none'),
      toggleShuffle: () => setShuffle((value) => !value),
      toggleAutoplay: () => setAutoplay((value) => !value),
      setExpanded,
      addToQueue,
      removeFromQueue,
      clearPlayer
    }}>
      {children}
    </PlayerContext.Provider>
  );
}

export const usePlayer = () => useContext(PlayerContext);